import { useMemo, useState } from "react";
import { Download } from "lucide-react";
import {
  useGetTradesQuery,
  useGetMyTradesQuery,
  BackendTrade,
} from "@/store/api";
import { useAppSelector } from "@/store";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { StalenessBadge } from "@/components/domain/StalenessBadge";
import { Skeleton } from "@/components/ui/skeleton";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Table, THead, TBody, TR, TH, TD } from "@/components/ui/table";
import { tradesBySymbol } from "@/lib/aggregate";
import { PnLPill } from "@/components/domain/PnLPill";
import {
  UserFilter, type UserFilterValue, matchesUserFilter,
} from "@/components/domain/UserFilter";
import { fmtUsd, fmtNum, fmtTime } from "@/lib/format";

export default function Trades() {
  const isAdmin = useAppSelector((s) => s.auth.user?.role === "admin");
  // Admins read the global trades table; everyone else only sees their own.
  const adminQ = useGetTradesQuery(undefined, {
    pollingInterval: 30000, skip: !isAdmin,
  });
  const myQ = useGetMyTradesQuery({ limit: 500 } as never, {
    pollingInterval: 30000, skip: isAdmin,
  });
  const data = (isAdmin ? adminQ.data : (myQ.data as unknown as BackendTrade[])) as BackendTrade[] | undefined;
  const isLoading = isAdmin ? adminQ.isLoading : myQ.isLoading;
  const [filter, setFilter] = useState("");
  const [since, setSince] = useState("");
  const [until, setUntil] = useState("");
  const [userFilter, setUserFilter] = useState<UserFilterValue>(null);
  // Source filter: "all" | "hourly" | "scalp" | "other" — DB column
  // `trades.source` is "hourly" / "scalp" / null (legacy) / occasionally
  // other tags like "mpt_rebalance". Treat null as "hourly" for parity
  // with how the rest of the UI defaults it.
  const [sourceFilter, setSourceFilter] =
    useState<"all" | "hourly" | "scalp" | "other">("all");

  const rows = useMemo(() => {
    let r = data ?? [];
    const f = filter.toUpperCase();
    if (f) {
      r = r.filter(
        (t) =>
          t.symbol.toUpperCase().includes(f) ||
          (t.order_id ?? "").toUpperCase().includes(f),
      );
    }
    if (since) {
      const ms = new Date(since).getTime();
      if (!Number.isNaN(ms)) r = r.filter((t) => new Date(t.executed_at).getTime() >= ms);
    }
    if (until) {
      const ms = new Date(until).getTime();
      if (!Number.isNaN(ms)) r = r.filter((t) => new Date(t.executed_at).getTime() <= ms);
    }
    if (isAdmin && userFilter !== null) {
      r = r.filter((t) => matchesUserFilter(t, userFilter));
    }
    if (sourceFilter !== "all") {
      r = r.filter((t) => {
        const src = (t.source ?? "hourly").toLowerCase();
        if (sourceFilter === "hourly") return src === "hourly";
        if (sourceFilter === "scalp") return src === "scalp";
        return src !== "hourly" && src !== "scalp";
      });
    }
    return r;
  }, [data, filter, since, until, userFilter, isAdmin, sourceFilter]);

  const summary = useMemo(() => tradesBySymbol(rows), [rows]);

  const pnlTotals = useMemo(() => {
    let realized = 0;
    let n = 0;
    for (const t of rows) {
      if (t.action === "SELL" && typeof t.realized_pnl_usd === "number") {
        realized += t.realized_pnl_usd;
        n += 1;
      }
    }
    return { realized, n };
  }, [rows]);

  const onExport = () => {
    const csv = toCSV(rows);
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    const stamp = new Date().toISOString().replace(/[:.]/g, "-").slice(0, 19);
    a.href = url;
    a.download = `trades-${stamp}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader className="flex-row items-start justify-between gap-2 flex-wrap">
          <CardTitle className="flex items-center gap-3 flex-wrap">
            <span>Trade history</span>
            <StalenessBadge
              ts={rows[0]?.executed_at}
              warnHours={2} staleHours={12} veryStaleHours={48}
              label="last trade"
            />
            <span className="text-xs font-normal text-muted">
              {rows.length} rows · realized P&amp;L over {pnlTotals.n} closed
              {" "}
              <PnLPill value={pnlTotals.n > 0 ? pnlTotals.realized : null} />
            </span>
          </CardTitle>
          <div className="flex flex-wrap items-center gap-2">
            {isAdmin && (
              <UserFilter value={userFilter} onChange={setUserFilter} includeUnassigned />
            )}
            <div className="flex rounded-md border border-border overflow-hidden">
              {(["all", "hourly", "scalp", "other"] as const).map((k) => (
                <button
                  key={k}
                  onClick={() => setSourceFilter(k)}
                  className={
                    "text-xs px-2.5 py-1 border-r border-border last:border-r-0 " +
                    (sourceFilter === k
                      ? "bg-bg2 text-fg"
                      : "text-muted hover:text-fg hover:bg-bg2/50")
                  }
                  title={
                    k === "all" ? "All sources"
                    : k === "hourly" ? "Hourly bot trades"
                    : k === "scalp" ? "Scalp engine trades"
                    : "MPT / rebalance / hand-placed"
                  }
                >
                  {k}
                </button>
              ))}
            </div>
            <Input
              placeholder="Filter by symbol or order id"
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              className="max-w-xs h-8 text-xs"
            />
            <Input
              type="datetime-local"
              value={since}
              onChange={(e) => setSince(e.target.value)}
              title="Since"
              className="max-w-[200px] h-8 text-xs"
            />
            <Input
              type="datetime-local"
              value={until}
              onChange={(e) => setUntil(e.target.value)}
              title="Until"
              className="max-w-[200px] h-8 text-xs"
            />
            <Button
              variant="ghost"
              size="sm"
              disabled={rows.length === 0}
              onClick={onExport}
              title="Export filtered rows to CSV"
            >
              <Download className="w-3.5 h-3.5" /> CSV
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <Skeleton className="h-48 w-full" />
          ) : rows.length === 0 ? (
            <p className="text-sm text-muted">No trades match the filter.</p>
          ) : (
            <Table>
              <THead>
                <TR>
                  <TH>Time</TH>
                  {isAdmin && <TH>Owner</TH>}
                  <TH>Side</TH>
                  <TH>Symbol</TH>
                  <TH className="text-right">Size USD</TH>
                  <TH className="text-right">Filled</TH>
                  <TH className="text-right">Avg price</TH>
                  <TH className="text-right">Fee</TH>
                  <TH className="text-right">P&amp;L</TH>
                  <TH>Source</TH>
                  <TH>Mode</TH>
                  <TH>Order id</TH>
                </TR>
              </THead>
              <TBody>
                {rows.map((t) => (
                  <TR key={t.id}>
                    <TD className="text-xs text-muted">{fmtTime(t.executed_at)}</TD>
                    {isAdmin && (
                      <TD className="text-xs">
                        {t.user_id != null ? (
                          <>
                            <span className="text-muted">#{t.user_id}</span>{" "}
                            <span>{t.user_email ?? ""}</span>
                          </>
                        ) : <span className="text-muted/60">—</span>}
                      </TD>
                    )}
                    <TD>
                      <Badge tone={t.action === "BUY" ? "good" : "bad"}>{t.action}</Badge>
                    </TD>
                    <TD>{t.symbol}</TD>
                    <TD className="text-right tabular">{fmtUsd(t.size_usd ?? null)}</TD>
                    <TD className="text-right tabular">{fmtNum(t.filled_qty ?? null, 6)}</TD>
                    <TD className="text-right tabular">{fmtNum(t.avg_price ?? null, 6)}</TD>
                    <TD className="text-right tabular">
                      {fmtUsd(t.fee ?? null)}{" "}
                      <span className="text-muted text-xs">{t.fee_currency ?? ""}</span>
                    </TD>
                    <TD className="text-right">
                      {t.action === "SELL" && t.realized_pnl_usd != null ? (
                        <PnLPill value={t.realized_pnl_usd} />
                      ) : t.source === "dca" && t.action === "BUY" && t.unrealized_pnl_usd != null ? (
                        <>
                          <PnLPill value={t.unrealized_pnl_usd} />
                          <div className={
                            "text-xs " +
                            (t.unrealized_pnl_pct! >= 0 ? "text-good" : "text-bad")
                          }>
                            {t.unrealized_pnl_pct! >= 0 ? "+" : ""}
                            {t.unrealized_pnl_pct!.toFixed(2)}% unreal
                          </div>
                        </>
                      ) : (
                        <span className="text-xs text-muted">—</span>
                      )}
                    </TD>
                    <TD>
                      <Badge tone={t.source === "scalp" ? "info" : "default"}>
                        {t.source ?? "hourly"}
                      </Badge>
                    </TD>
                    <TD>
                      <Badge tone={t.dry_run ? "warn" : "bad"}>
                        {t.dry_run ? "dry" : "live"}
                      </Badge>
                    </TD>
                    <TD className="text-xs font-mono truncate max-w-[180px]">
                      {t.order_id ?? "-"}
                    </TD>
                  </TR>
                ))}
              </TBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>By symbol</CardTitle>
        </CardHeader>
        <CardContent>
          {summary.length === 0 ? (
            <p className="text-sm text-muted">No trades to summarize.</p>
          ) : (
            <Table>
              <THead>
                <TR>
                  <TH>Symbol</TH>
                  <TH className="text-right">Trades</TH>
                  <TH className="text-right">Buys</TH>
                  <TH className="text-right">Sells</TH>
                  <TH className="text-right">Fees</TH>
                  <TH className="text-right">Net flow</TH>
                </TR>
              </THead>
              <TBody>
                {summary.map((s) => (
                  <TR key={s.symbol}>
                    <TD>{s.symbol}</TD>
                    <TD className="text-right tabular">{s.trades}</TD>
                    <TD className="text-right tabular">{fmtUsd(s.buyUsd)}</TD>
                    <TD className="text-right tabular">{fmtUsd(s.sellUsd)}</TD>
                    <TD className="text-right tabular">{fmtUsd(s.fees)}</TD>
                    <TD className="text-right">
                      <PnLPill value={s.netFlow} />
                    </TD>
                  </TR>
                ))}
              </TBody>
            </Table>
          )}
          <p className="text-xs text-muted mt-2">
            "Net flow" is sells − buys − fees on this filtered set; it doesn't track open
            inventory or cost basis. Use the Performance tab for realized P&amp;L.
          </p>
        </CardContent>
      </Card>
    </div>
  );
}

function toCSV(rows: BackendTrade[]): string {
  const headers = [
    "id",
    "executed_at",
    "user_id",
    "user_email",
    "action",
    "symbol",
    "size_usd",
    "filled_qty",
    "avg_price",
    "fee",
    "fee_currency",
    "realized_pnl_usd",
    "source",
    "dry_run",
    "order_id",
  ];
  const lines = [headers.join(",")];
  for (const t of rows) {
    lines.push(
      [
        t.id,
        t.executed_at,
        t.user_id ?? "",
        t.user_email ?? "",
        t.action,
        t.symbol,
        t.size_usd ?? "",
        t.filled_qty ?? "",
        t.avg_price ?? "",
        t.fee ?? "",
        t.fee_currency ?? "",
        t.realized_pnl_usd ?? "",
        t.source ?? "",
        t.dry_run,
        (t.order_id ?? "").replace(/,/g, ""),
      ]
        .map(String)
        .join(","),
    );
  }
  return lines.join("\n");
}
